import { useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * AudioButton - Composant de synthèse vocale pour prononcer les mots baoulé
 * 
 * @param {string} text - Le texte à prononcer (mot baoulé ou phonétique)
 * @param {string} phonetic - La phonétique du mot (optionnel)
 * @param {string} className - Classes CSS additionnelles
 * @param {string} size - Taille du bouton (sm, md, lg)
 */
export default function AudioButton({ 
  text, 
  phonetic, 
  className = "",
  size = "md" 
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(false);

  const speak = () => {
    // Vérifier si la synthèse vocale est supportée
    if (!('speechSynthesis' in window)) {
      console.error('Web Speech API non supportée');
      setError(true);
      setTimeout(() => setError(false), 2000);
      return;
    }

    // Annuler toute lecture en cours
    window.speechSynthesis.cancel();

    // Créer un nouvel utterance
    const utterance = new SpeechSynthesisUtterance();
    
    // Utiliser la phonétique si disponible, sinon le texte original
    utterance.text = text || phonetic;
    
    // Configuration de la voix
    utterance.lang = 'fr-FR'; // Langue française pour meilleure prononciation
    utterance.rate = 0.8; // Vitesse ralentie pour meilleure compréhension
    utterance.pitch = 1.0; // Tonalité normale
    utterance.volume = 1.0; // Volume maximum

    // Événements
    utterance.onstart = () => {
      setIsPlaying(true);
      setError(false);
    };

    utterance.onend = () => {
      setIsPlaying(false);
    };

    utterance.onerror = (event) => {
      console.error('Erreur de synthèse vocale:', event);
      setIsPlaying(false);
      setError(true);
      setTimeout(() => setError(false), 2000);
    };

    // Lancer la lecture
    try {
      window.speechSynthesis.speak(utterance);
    } catch (err) {
      console.error('Erreur lors du lancement:', err);
      setIsPlaying(false);
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
  };

  const handleClick = (e) => {
    e.stopPropagation(); // Empêcher la propagation si dans une carte cliquable
    speak();
  };

  // Tailles du bouton
  const sizes = {
    sm: "p-1.5 w-7 h-7",
    md: "p-2 w-9 h-9",
    lg: "p-3 w-12 h-12"
  };

  // Tailles de l'icône
  const iconSizes = {
    sm: "w-3 h-3",
    md: "w-4 h-4",
    lg: "w-5 h-5"
  };

  return (
    <button
      onClick={handleClick}
      disabled={isPlaying}
      className={`
        rounded-full 
        transition-all 
        duration-300
        ${error 
          ? 'bg-destructive/20 hover:bg-destructive/30' 
          : 'bg-primary/10 hover:bg-primary/20'
        }
        ${isPlaying ? 'audio-playing animate-pulse' : ''}
        disabled:opacity-50 
        disabled:cursor-not-allowed
        active:scale-95
        ${sizes[size]}
        ${className}
      `}
      aria-label={isPlaying ? "En cours de lecture..." : "Écouter la prononciation"}
      title={isPlaying ? "En cours de lecture..." : "Écouter la prononciation"}
    >
      {error ? (
        <VolumeX className={`${iconSizes[size]} text-destructive`} />
      ) : (
        <Volume2 className={`${iconSizes[size]} text-primary ${isPlaying ? 'animate-pulse' : ''}`} />
      )}
    </button>
  );
}
